import React, {
  useState,
  useEffect,
} from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  SportsEsports as GameIcon,
  EmojiEvents as CompetitiveIcon,
  SportsBasketball as CasualIcon,
  DirectionsRun as IRLIcon,
  Image as ImageIcon,
  CloudUpload as UploadIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import gulagService from '../../services/gulag';
import {
  isStaticImagePath,
} from '../../utils/gameImages';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import StaticImagePicker from './StaticImagePicker';

function GamesManagement() {
    const [games, setGames] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [stats, setStats] = useState({
        totalActive: 0,
        totalArchived: 0,
        byCasual: 0,
        byCompetitive: 0,
        byIRL: 0
    });

    // Dialog states
    const [createDialog, setCreateDialog] = useState(false);
    const [editDialog, setEditDialog] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [selectedGame, setSelectedGame] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        category: 'casual',
        team_based: false,
        max_players_per_side: 1,
        points_multiplier: 1.0,
        description: '',
        rules: '',
        background_image: '',
        challenges_allowed: true,
        time_trial: false
    });
    
    // Image upload state
    const [selectedImageFile, setSelectedImageFile] = useState(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError(null);

        try {
            // Load all games (active and archived)
            const [activeResult, archivedResult] = await Promise.all([
                gulagService.getGames('active'),
                gulagService.getGames('archived')
            ]);

            const allGames = [
                ...(activeResult.data || []),
                ...(archivedResult.data || [])
            ];
            
            setGames(allGames);

            // Calculate stats
            const activeGames = allGames.filter(g => g.status === 'active');
            const archivedGames = allGames.filter(g => g.status === 'archived');
            
            setStats({
                totalActive: activeGames.length,
                totalArchived: archivedGames.length,
                byCasual: activeGames.filter(g => g.category === 'casual').length,
                byCompetitive: activeGames.filter(g => g.category === 'competitive').length,
                byIRL: activeGames.filter(g => g.category === 'irl').length
            });
        } catch (err) {
            console.error('Error loading games:', err);
            setError('Failed to load games');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenCreateDialog = () => {
        setFormData({
            name: '',
            category: 'casual',
            team_based: false,
            max_players_per_side: 1,
            points_multiplier: 1.0,
            description: '',
            rules: '',
            background_image: '',
            challenges_allowed: true,
            time_trial: false
        });
        setSelectedImageFile(null);
        setImagePreviewUrl(null);
        setCreateDialog(true);
    };

    const handleOpenEditDialog = (game) => {
        setSelectedGame(game);
        setFormData({
            name: game.name,
            category: game.category,
            team_based: game.team_based,
            max_players_per_side: game.max_players_per_side,
            points_multiplier: game.points_multiplier,
            description: game.description || '',
            rules: game.rules || '',
            background_image: game.background_image || '',
            challenges_allowed: game.challenges_allowed !== undefined ? game.challenges_allowed : true,
            time_trial: game.time_trial || false
        });
        setSelectedImageFile(null);
        setImagePreviewUrl(null);
        setEditDialog(true);
    };

    const handleOpenDeleteDialog = (game) => {
        setSelectedGame(game);
        setDeleteDialog(true);
    };

    const handleCreateGame = async () => {
        try {
            const result = await gulagService.createGame(formData);
            if (result.success) {
                // If there's an image file selected, upload it
                if (selectedImageFile && result.data?.id) {
                    setUploadingImage(true);
                    const uploadResult = await gulagService.uploadGameImage(result.data.id, selectedImageFile);
                    setUploadingImage(false);
                    
                    if (!uploadResult.success) {
                        setError('Game created but image upload failed: ' + uploadResult.error);
                    } else {
                        setSuccess('Game created with image successfully!');
                    }
                } else {
                    setSuccess('Game created successfully!');
                }
                setCreateDialog(false);
                loadData();
            } else {
                setError(result.error || 'Failed to create game');
            }
        } catch (err) {
            setError('Failed to create game');
        }
    };

    const handleUpdateGame = async () => {
        if (!selectedGame) return;

        try {
            const result = await gulagService.updateGame(selectedGame.id, formData);
            if (result.success) {
                // If there's an image file selected, upload it
                if (selectedImageFile) {
                    setUploadingImage(true);
                    const uploadResult = await gulagService.uploadGameImage(selectedGame.id, selectedImageFile);
                    setUploadingImage(false);
                    
                    if (!uploadResult.success) {
                        setError('Game updated but image upload failed: ' + uploadResult.error);
                    } else {
                        setSuccess('Game updated with image successfully!');
                    }
                } else {
                    setSuccess('Game updated successfully!');
                }
                setEditDialog(false);
                loadData();
            } else {
                setError(result.error || 'Failed to update game');
            }
        } catch (err) {
            setError('Failed to update game');
        }
    };

    const handleToggleStatus = async (game) => {
        const newStatus = game.status === 'active' ? 'archived' : 'active';
        
        try {
            const result = await gulagService.updateGame(game.id, { status: newStatus });
            if (result.success) {
                setSuccess(`Game ${newStatus === 'archived' ? 'archived' : 'activated'} successfully!`);
                loadData();
            } else {
                setError(result.error || 'Failed to update game status');
            }
        } catch (err) {
            setError('Failed to update game status');
        }
    };

    const handleDeleteGame = async () => {
        if (!selectedGame) return;

        try {
            // Archive instead of delete
            const result = await gulagService.updateGame(selectedGame.id, { status: 'archived' });
            if (result.success) {
                setSuccess('Game archived successfully!');
                setDeleteDialog(false);
                loadData();
            } else {
                setError(result.error || 'Failed to archive game');
            }
        } catch (err) {
            setError('Failed to archive game');
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };
    
    const handleImageFileSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                setError('Please select a valid image file');
                return;
            }
            
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setError('Image file size must be less than 5MB');
                return;
            }
            
            setSelectedImageFile(file);
            
            // Create preview URL
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
            
            // Clear the URL field since we're uploading a file
            setFormData(prev => ({
                ...prev,
                background_image: ''
            }));
        }
    };
    
    const handleImageUrlChange = (url) => {
        handleInputChange('background_image', url);
        // Clear file selection if URL is being used
        if (url && selectedImageFile) {
            setSelectedImageFile(null);
            setImagePreviewUrl(null);
        }
    };

    const getCategoryColor = (category) => {
        switch (category) {
            case 'casual': return 'success';
            case 'competitive': return 'error';
            case 'irl': return 'warning';
            default: return 'default';
        }
    };

    const getCategoryIcon = (category) => {
        switch (category) {
            case 'casual': return <CasualIcon />;
            case 'competitive': return <CompetitiveIcon />;
            case 'irl': return <IRLIcon />;
            default: return <GameIcon />;
        }
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Games Management
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleOpenCreateDialog}
                    >
                        Create Game
                    </Button>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    Manage games for Gulag, tournaments, and other features
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Statistics */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <GameIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                                <Box>
                                    <Typography variant="h4">{stats.totalActive}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Active Games
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <ArchiveIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                                <Box>
                                    <Typography variant="h4">{stats.totalArchived}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Archived Games
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <CasualIcon sx={{ fontSize: 32, color: 'success.main' }} />
                                <Box>
                                    <Typography variant="h5">{stats.byCasual}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Casual
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <CompetitiveIcon sx={{ fontSize: 32, color: 'error.main' }} />
                                <Box>
                                    <Typography variant="h5">{stats.byCompetitive}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Competitive
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <IRLIcon sx={{ fontSize: 32, color: 'warning.main' }} />
                                <Box>
                                    <Typography variant="h5">{stats.byIRL}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        IRL
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Games Table */}
            <Paper>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        All Games ({games.length})
                    </Typography>
                </Box>
                {loading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <CircularProgress />
                    </Box>
                ) : games.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <GameIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            No games yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            Create your first game to get started
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleOpenCreateDialog}
                            sx={{ mt: 2 }}
                        >
                            Create Game
                        </Button>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><strong>Game Name</strong></TableCell>
                                    <TableCell><strong>Category</strong></TableCell>
                                    <TableCell><strong>Type</strong></TableCell>
                                    <TableCell><strong>Players</strong></TableCell>
                                    <TableCell><strong>Multiplier</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell align="right"><strong>Actions</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {games.map((game) => (
                                    <TableRow key={game.id}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {getCategoryIcon(game.category)}
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                        {game.name}
                                                    </Typography>
                                                    {game.description && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {game.description}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={game.category}
                                                size="small"
                                                color={getCategoryColor(game.category)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={game.team_based ? 'Team' : '1v1'}
                                                size="small"
                                                variant="outlined"
                                            />
                                            {game.challenges_allowed === false && (
                                                <Chip
                                                    label="Sessions Only"
                                                    size="small"
                                                    color="warning"
                                                    variant="outlined"
                                                    sx={{ ml: 0.5 }}
                                                />
                                            )}
                                            {game.time_trial && (
                                                <Chip
                                                    icon={<TimerIcon />}
                                                    label="Time Trial"
                                                    size="small"
                                                    color="info"
                                                    sx={{ ml: 0.5 }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>{game.max_players_per_side}</TableCell>
                                        <TableCell>{game.points_multiplier}x</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={game.status}
                                                size="small"
                                                color={game.status === 'active' ? 'success' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                                <Tooltip title={game.status === 'active' ? 'Archive' : 'Activate'}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleToggleStatus(game)}
                                                        color={game.status === 'active' ? 'warning' : 'success'}
                                                    >
                                                        {game.status === 'active' ? <ArchiveIcon /> : <UnarchiveIcon />}
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Edit">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenEditDialog(game)}
                                                        color="primary"
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                {game.status !== 'archived' && (
                                                    <Tooltip title="Delete">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleOpenDeleteDialog(game)}
                                                            color="error"
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* Create/Edit Game Dialog */}
            <Dialog 
                open={createDialog || editDialog} 
                onClose={() => {
                    setCreateDialog(false);
                    setEditDialog(false);
                }} 
                maxWidth="md" 
                fullWidth
            >
                <DialogTitle>
                    {createDialog ? 'Create New Game' : 'Edit Game'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Game Name"
                            value={formData.name}
                            onChange={(e) => handleInputChange('name', e.target.value)}
                            fullWidth
                            required
                        />
                        
                        <FormControl fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select
                                value={formData.category}
                                onChange={(e) => handleInputChange('category', e.target.value)}
                                label="Category"
                            >
                                <MenuItem value="casual">Casual</MenuItem>
                                <MenuItem value="competitive">Competitive</MenuItem>
                                <MenuItem value="irl">IRL (In Real Life)</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.team_based}
                                    onChange={(e) => handleInputChange('team_based', e.target.checked)}
                                />
                            }
                            label="Team-based game"
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.challenges_allowed}
                                    onChange={(e) => handleInputChange('challenges_allowed', e.target.checked)}
                                />
                            }
                            label="Allow challenges for this game"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: -1, mb: 1 }}>
                            Disable for session-only games (e.g., Flatout)
                        </Typography>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.time_trial}
                                    onChange={(e) => handleInputChange('time_trial', e.target.checked)}
                                />
                            }
                            label="Time Trial Compatible"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: -1, mb: 1 }}>
                            Enable to allow this game to be used in time trial competitions
                        </Typography>

                        <TextField
                            label="Max Players Per Side"
                            type="number"
                            value={formData.max_players_per_side}
                            onChange={(e) => handleInputChange('max_players_per_side', parseInt(e.target.value) || 1)}
                            fullWidth
                            InputProps={{ inputProps: { min: 1, max: 8 } }}
                        />

                        <TextField
                            label="Points Multiplier"
                            type="number"
                            value={formData.points_multiplier}
                            onChange={(e) => handleInputChange('points_multiplier', parseFloat(e.target.value) || 1.0)}
                            fullWidth
                            InputProps={{ inputProps: { min: 0.5, max: 5.0, step: 0.1 } }}
                        />

                        <TextField
                            label="Description"
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                        />

                        <TextField
                            label="Rules"
                            value={formData.rules}
                            onChange={(e) => handleInputChange('rules', e.target.value)}
                            fullWidth
                            multiline
                            rows={4}
                        />

                        {/* Background Image Section */}
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                                Background Image Options
                            </Typography>

                            {/* Static Image Picker */}
                            <StaticImagePicker
                                selectedImage={isStaticImagePath(formData.background_image) ? formData.background_image : null}
                                onImageSelect={(imagePath) => {
                                    setFormData({ ...formData, background_image: imagePath });
                                    setSelectedImageFile(null);
                                    setImagePreviewUrl(null);
                                }}
                            />

                            {/* Clear Static Image Selection Button */}
                            {isStaticImagePath(formData.background_image) && (
                                <Button
                                    variant="outlined"
                                    color="warning"
                                    fullWidth
                                    sx={{ mt: 2 }}
                                    onClick={() => {
                                        setFormData({ ...formData, background_image: '' });
                                        setSelectedImageFile(null);
                                        setImagePreviewUrl(null);
                                    }}
                                >
                                    Clear Static Image Selection
                                </Button>
                            )}

                            {/* Divider */}
                            <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
                                <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                                <Typography variant="caption" sx={{ px: 2, color: 'text.secondary', fontWeight: 600 }}>
                                    OR USE CUSTOM IMAGE
                                </Typography>
                                <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                            </Box>

                            <Alert severity="warning" sx={{ mb: 2 }}>
                                Custom images uploaded here may <strong>not persist</strong> across redeployments. 
                                Use static images above for guaranteed persistence.
                            </Alert>

                            {/* File Upload Button */}
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<UploadIcon />}
                                fullWidth
                                sx={{ mb: 2 }}
                                disabled={isStaticImagePath(formData.background_image)}
                            >
                                {selectedImageFile ? `Selected: ${selectedImageFile.name}` : 'Upload Image File'}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*"
                                    onChange={handleImageFileSelect}
                                />
                            </Button>

                            {/* OR Divider */}
                            <Box sx={{ display: 'flex', alignItems: 'center', my: 2 }}>
                                <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                                <Typography variant="caption" sx={{ px: 2, color: 'text.secondary' }}>
                                    OR
                                </Typography>
                                <Box sx={{ flex: 1, height: 1, bgcolor: 'divider' }} />
                            </Box>

                            {/* URL Input */}
                            <TextField
                                label="External Image URL"
                                value={isStaticImagePath(formData.background_image) ? '' : formData.background_image}
                                onChange={(e) => handleImageUrlChange(e.target.value)}
                                fullWidth
                                placeholder="https://example.com/image.jpg"
                                helperText="Enter an external URL if not using static or uploaded image"
                                disabled={isStaticImagePath(formData.background_image)}
                                InputProps={{
                                    startAdornment: <ImageIcon sx={{ mr: 1, color: 'action.active' }} />
                                }}
                            />

                            {isStaticImagePath(formData.background_image) && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    Using static image. Click "Clear Static Image Selection" above to use custom upload or URL instead.
                                </Typography>
                            )}
                        </Box>

                        {/* Image Preview */}
                        {(imagePreviewUrl || formData.background_image) && (
                            <Box>
                                {isStaticImagePath(formData.background_image) && (
                                    <Alert severity="success" sx={{ mb: 2 }}>
                                        ✓ Using static image - guaranteed to persist across deployments!
                                    </Alert>
                                )}
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: 200,
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        border: 1,
                                        borderColor: isStaticImagePath(formData.background_image) ? 'success.main' : 'divider',
                                        mt: 2
                                    }}
                                >
                                    <img
                                        src={imagePreviewUrl || resolveMediaUrl(formData.background_image)}
                                        alt="Preview"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover'
                                        }}
                                        onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.parentElement.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #999;">Invalid image</div>';
                                        }}
                                    />
                                </Box>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => {
                            setCreateDialog(false);
                            setEditDialog(false);
                        }}
                        disabled={uploadingImage}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={createDialog ? handleCreateGame : handleUpdateGame}
                        variant="contained"
                        disabled={!formData.name || uploadingImage}
                        startIcon={uploadingImage ? <CircularProgress size={20} /> : null}
                    >
                        {uploadingImage ? 'Uploading...' : (createDialog ? 'Create' : 'Update')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
                <DialogTitle>Archive Game</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to archive "{selectedGame?.name}"? 
                        This will make it unavailable for selection but preserve its data.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
                    <Button onClick={handleDeleteGame} color="warning" variant="contained">
                        Archive
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default GamesManagement;

